import { SyntheticEvent, memo, useCallback, useRef, useState } from 'react'
import { SettingsType, VideoType } from '../../../../constants/enums'
// import { hexRGBA } from '../../../../utilities/colors'
import ButtonModelMenu from '../../../Buttons/ButtonModelMenu'
import { useUserSelector } from '../../../Hooks/useUser'
import layers from '../../../styles/layers'
import LiveSnapStream from '../../LiveSnapStream'
import BookmarkBadge from '../Badges/Bookmark'
import FriendBadge from '../Badges/Friend'
import { NewAndMissMFCBadges } from '../Badges/NewAndMissMFC'
import PopularBadge from '../Badges/Popular'
// import StatusBadge from '../Badges/Status'
import { css } from '@emotion/react'
import { openMenuDialog } from '../../../../actions/menu'
import { goToNotes } from '../../../../actions/nav'
import { openRoom, pinRoom, setLoungeSurrogate } from '../../../../actions/room'
import { Size } from '../../../../app-state/state.screen'
import { getAnimationsEnabledfromUeopt } from '../../../../mfcLogic/settings'
import { SNAPVIDEOSTATE } from '../../../../state/state.snapvideo'
import { isTouchDevice } from '../../../../utilities/browser'
import {
    TransitionTypes,
    motionTransition,
} from '../../../../utilities/transitions'
import { useGridCenteredIndexAppState } from '../../../Hooks/useGridCenteredIndexAppState'
import { useFloatingPreviewSelector } from '../../../Hooks/useModal'
import { useIsRoomPinned } from '../../../Hooks/useRoom'
import { useSettingSelector } from '../../../Hooks/useSettings'
import { useVideoPlaying } from '../../../Hooks/useVideo'
import RoomIcon from '../../../Icons/Room'
import { palette } from '../../../styles/colors'
import shape from '../../../styles/shape'
import { PAPER_HEIGHTS } from '../../../theme'
import {
    hideDragHelper,
    highlightDragHelper,
    showDragHelper,
} from '../../Chat/Rooms/PinnedRooms/PinDragHelper'
import PriorMissMFCBadge from '../Badges/PriorMissMFC'
import TrendingBadge from '../Badges/Trending'
import GroupShowBanner from '../Banners/GroupShow'
import MissMFCBanner from '../Banners/MissMFC'
import NewModelBanner from '../Banners/NewModel'
import PopularBanner from '../Banners/Popular'
import TrendingBanner from '../Banners/Trending'
import NotesButton from '../Buttons/NotesButton'
import PinButton from '../Buttons/PinButton'
import Container from '../Container'
import LargeFloatingPreview from '../LargeFloatingPreview'
import ModelName from '../ModelName'
import { VideoCover } from '../VideoCover'
import {
    GetStylesConfig,
    getAnonymousMissMFCRank,
    isAnonymousMissMFC,
    resolveDisplayHighlight,
} from '../helpers'

const rootStyles = css({
    flexDirection: 'column',
    position: 'relative',
    display: 'flex',
})
const previewContainerStyles = css({
    borderRadius: shape.borderRadiusSmallMedium,
    boxShadow: layers.one,
    position: 'relative',
    overflow: 'hidden',
})
const previewPlayerContainer = css({
    zIndex: PAPER_HEIGHTS.highest + 1,
    flexDirection: 'column',
    position: 'absolute',
    display: 'flex',
    left: 0,
    top: 0,
})
const actionContainerStyles = css({
    zIndex: PAPER_HEIGHTS.highest + 1,
    flexDirection: 'column',
    position: 'absolute',
    alignItems: 'center',
    display: 'flex',
    right: 0,
    top: 0,
})
const badgeContainerStyles = css({
    zIndex: PAPER_HEIGHTS.highest + 1,
    flexDirection: 'column',
    position: 'absolute',
    alignItems: 'center',
    display: 'flex',
    bottom: 0,
    left: 0,
})
const modelMenuStyles = css({
    zIndex: PAPER_HEIGHTS.highest + 1,
    position: 'absolute',
    bottom: 3,
    right: 3,
})

interface PreviewProps {
    activeRoom?: number
    columnWidth: number
    disableMenuNextPrev?: boolean
    displayBookmarkBadge?: boolean
    displayBookmarkHighlight?: boolean
    displayFriendBadge?: boolean
    displayFriendHighlight?: boolean
    displayGroupShowBanner?: boolean
    displayHeight: number
    displayMissMFCBadge?: boolean
    displayMissMFCBanner?: boolean
    displayNewModelBadge?: boolean
    displayNewModelBanner?: boolean
    displayNoteIconButton?: boolean
    displayPopularBadge?: boolean
    displayPopularBanner?: boolean
    displayPopularHighlight?: boolean
    displayRecentlyVisitedBadge?: boolean
    displayRecentlyVisitedHighlight?: boolean
    displayTrendingBadge?: boolean
    displayTrendingBanner?: boolean
    displayTrendingHighlight?: boolean
    enableCenteredIndexTracking?: boolean
    enableLargePreview?: boolean
    enablePin?: boolean
    enablePinToTopSymbols?: boolean
    getStyle: (key: string, config?: GetStylesConfig) => any
    id: number
    index: number
    loungeSlug?: string
    name: string
    passiveMode?: boolean
    singlePin?: boolean
    pinLimitReached?: boolean
    slug: string
}

const Preview = memo((props: PreviewProps) => {
    const {
        enableCenteredIndexTracking,
        displayRecentlyVisitedHighlight,
        displayRecentlyVisitedBadge,
        displayTrendingHighlight,
        displayBookmarkHighlight,
        displayPopularHighlight,
        displayFriendHighlight,
        displayGroupShowBanner,
        displayTrendingBanner,
        displayNewModelBanner,
        displayPopularBanner,
        displayMissMFCBanner,
        displayBookmarkBadge,
        displayTrendingBadge,
        displayNewModelBadge,
        displayPopularBadge,
        displayMissMFCBadge,
        displayFriendBadge,
        displayNoteIconButton,
        enablePinToTopSymbols,
        disableMenuNextPrev,
        enableLargePreview,
        pinLimitReached,
        displayHeight,
        passiveMode,
        columnWidth,
        activeRoom,
        loungeSlug,
        singlePin,
        enablePin,
        getStyle,
        index,
        slug,
        name,
        id,
    } = props

    const isGridCenteredOnIndex = useGridCenteredIndexAppState(
        index,
        enableCenteredIndexTracking,
    )
    const isVideoAlreadyPlaying = useVideoPlaying(id)
    const shouldForceVideoPlay = isGridCenteredOnIndex && !isVideoAlreadyPlaying

    const ref = useRef(null)
    const snapRef = useRef<HTMLImageElement>(undefined)
    const snapSize = useRef<Size & { className: string }>(null)
    const [showVideoPreview, setVideoPreview] = useState(false)

    const [animatePreviews] = useSettingSelector(
        SettingsType.DEFAULT,
        'animatePreviews',
    )
    const staticImage = !getAnimationsEnabledfromUeopt(animatePreviews)
    const isPinnedToTopRecentlyVisited = useUserSelector(
        id,
        'isPinnedToTopRecentlyVisited',
    )
    const isPinnedToTopTrending = useUserSelector(id, 'isPinnedToTopTrending')
    const isPinnedToTopPopular = useUserSelector(id, 'isPinnedToTopPopular')
    const isPinnedToTopMissMFC = useUserSelector(id, 'isPinnedToTopMissMFC')
    const isBanned = useUserSelector(id, 'banned')
    const isGroupShowPending = useUserSelector(id, 'isGroupShowPending')
    const priorMissMfcRank = useUserSelector(id, 'priorMissMfcRank')
    const groupShowRoomCount = useUserSelector(id, 'groupShowRoomCount')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isTrendingNew = useUserSelector(id, 'isTrendingNew')
    const isTruePrivate = useUserSelector(id, 'isTruePrivate')
    const isPrivateShow = useUserSelector(id, 'isPrivateShow')
    const isPublicShow = useUserSelector(id, 'isPublicShow')
    const trendingDiff = useUserSelector(id, 'trendingDiff')
    const missMFCPrize = useUserSelector(id, 'missMFCPrize')
    const isAnonymous = isAnonymousMissMFC(id)
    const missMFCRankFromSelector = useUserSelector(id, 'missMFCRank')
    const missMFCRank = isAnonymous
        ? getAnonymousMissMFCRank(slug)
        : missMFCRankFromSelector
    const isMissMFCFromSelector = useUserSelector(id, 'isMissMFC')
    const isMissMFC = isAnonymous ? true : isMissMFCFromSelector
    const isGroupShow = useUserSelector(id, 'isGroupShow')
    const isBookmark = useUserSelector(id, 'isBookmark')
    const isNewModel = useUserSelector(id, 'isNewModel')
    const isClubShow = useUserSelector(id, 'isClubShow')
    const isPopular = useUserSelector(id, 'isPopular')
    const isOffline = useUserSelector(id, 'isOffline')
    const isNGVideo = useUserSelector(id, 'isNGVideo')
    const isLounge = useUserSelector(id, 'isLounge')
    const isKicked = useUserSelector(id, 'isKicked')
    const isFriend = useUserSelector(id, 'isFriend')
    const creation = useUserSelector(id, 'creation')
    const viewers = useUserSelector(id, 'viewers')
    const channel = useUserSelector(id, 'channel')
    const isModel = useUserSelector(id, 'isModel')
    const hasNote = useUserSelector(id, 'hasNote')
    const server = useUserSelector(id, 'server')
    const isAway = useUserSelector(id, 'isAway')
    const phase = useUserSelector(id, 'phase')
    const color = useUserSelector(id, 'color')

    const isRoomPinned = useIsRoomPinned(id)
    const isPreviewPinned = useFloatingPreviewSelector(id, 'close')

    const inHiddenShow =
        isTruePrivate || isPrivateShow || isGroupShow || isClubShow
    const noShowType = isOffline || inHiddenShow || isAway
    const isViewable = !noShowType && !isBanned && !isKicked
    const showSinglePin = singlePin && isViewable

    const stylesBannerPX = getStyle('banner', { px: true })
    const stylesNamePX = getStyle('name', { px: true })
    const stylesName = getStyle('name')
    const stylesBanner = getStyle('banner')
    const stylesBannerPopular = stylesBanner('popular')
    const stylesBannerPopularViewers = stylesBannerPopular('viewers')
    const stylesBannerTrending = stylesBanner('trending')
    const stylesBannerTrendingDiff = stylesBannerTrending('diff')
    const stylesBannerNew = stylesBanner('new')
    const stylesBannerNewTime = stylesBannerNew('time')
    const stylesBannerMissMFC = stylesBanner('missMFC')
    const stylesBannerMissMFCRank = stylesBannerMissMFC('rank')
    const stylesUtilities = getStyle('utilities')
    const stylesBadge = getStyle('badge')
    const stylesBadgeStatus = stylesBadge('status')
    const stylesBadgeNewMissMFC = stylesBadge('newMissMFC')
    const stylesPriorMissMFCBadge = stylesBadge('priorMissMFC')
    const stylesMaxWidth = getStyle('column')('maxWidth')

    // if (!id) {
    //     return <AvatarSkeleton width={width} height={height} />
    // }

    // const modelColor = useModelColor(color)

    //
    // For testing purposes:
    //
    // const candisplayFriendBadge = true
    // const canDisplayBookmarkBadge = true
    // const canDisplayTrendingBadge = true
    // const canDisplayPopularBadge = true
    // const canDisplayNewModelBadge = true
    // const canDisplayMissMFCBadge = true
    // const canDisplayPriorMissMFCBadge = true
    // const canDisplayRecentlyVisitedBadge = true
    // const canDisplayNotesButton = true
    // const canEnablePinToTopSymbols = false

    //
    // Symbols
    //
    const canEnablePinToTopSymbols = enablePinToTopSymbols

    //
    // Buttons
    //
    const canDisplayNotesButton = hasNote && displayNoteIconButton
    const canDisplayLargePreviewButton =
        enableLargePreview && isPublicShow && !isAnonymous
    const canDisplayPinButton =
        ((enablePin && !pinLimitReached) || isRoomPinned || showSinglePin) &&
        !isAnonymous &&
        isViewable

    //
    // Banners
    //
    const canDisplayGroupShowBanner =
        displayGroupShowBanner &&
        (isGroupShow || isGroupShowPending || groupShowRoomCount)
    const canDisplayTrendingBanner =
        displayTrendingBanner && isTrendingNew && trendingDiff
    const canDisplayNewModelBanner = displayNewModelBanner && isNewModel
    const canDisplayPopularBanner = displayPopularBanner && isPopular && viewers
    const canDisplayMissMFCBanner =
        displayMissMFCBanner && isMissMFC && missMFCRank

    //
    // Badges
    //
    const candisplayFriendBadge = displayFriendBadge && isFriend
    const canDisplayBookmarkBadge = displayBookmarkBadge && isBookmark
    const canDisplayTrendingBadge =
        displayTrendingBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopTrending) &&
        isTrendingNew &&
        trendingDiff
    const canDisplayPopularBadge =
        displayPopularBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopPopular) &&
        isPopular &&
        viewers
    const canDisplayNewModelBadge = displayNewModelBadge && isNewModel
    const canDisplayMissMFCBadge =
        displayMissMFCBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopMissMFC) &&
        isMissMFC &&
        missMFCRank > 0
    const canDisplayPriorMissMFCBadge = !!priorMissMfcRank
    const canDisplayRecentlyVisitedBadge =
        displayRecentlyVisitedBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited)

    //
    // Highlights
    //
    const canDisplayActiveRoomHighlight = activeRoom === id
    const canDisplayFriendHighlight = displayFriendHighlight && isFriend
    const canDisplayBookmarkHighlight = displayBookmarkHighlight && isBookmark
    const canDisplayPopularHighlight =
        displayPopularHighlight &&
        isPopular &&
        (!canEnablePinToTopSymbols || isPinnedToTopPopular)
    const canDisplayTrendingHighlight =
        displayTrendingHighlight &&
        isTrendingNew &&
        (!canEnablePinToTopSymbols || isPinnedToTopTrending)
    const canDisplayRecentlyViewedHighlight =
        displayRecentlyVisitedHighlight &&
        (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited)

    const highlight = resolveDisplayHighlight({
        displayRecentlyVisitedHighlight: canDisplayRecentlyViewedHighlight,
        displayActiveRoomHighlight: canDisplayActiveRoomHighlight,
        displayTrendingHighlight: canDisplayTrendingHighlight,
        displayBookmarkHighlight: canDisplayBookmarkHighlight,
        displayPopularHighlight: canDisplayPopularHighlight,
        displayFriendHighlight: canDisplayFriendHighlight,
    })

    const canDisplayBanners =
        canDisplayGroupShowBanner ||
        canDisplayTrendingBanner ||
        canDisplayNewModelBanner ||
        canDisplayMissMFCBanner ||
        canDisplayPopularBanner

    const canDisplayBadges =
        canDisplayPriorMissMFCBadge ||
        canDisplayNewModelBadge ||
        canDisplayBookmarkBadge ||
        canDisplayTrendingBadge ||
        canDisplayMissMFCBadge ||
        canDisplayPopularBadge ||
        candisplayFriendBadge

    const onPinCallback = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            motionTransition(() => pinRoom(id, { pinned: !isRoomPinned }), {
                id,
                skipTransition: isRoomPinned, // skip if we're unpinning it
                transitionSource: TransitionTypes.TILE,
                transitionDestination: TransitionTypes.PINROOM,
            })
        },
        [id, isRoomPinned],
    )

    const onOpenMenuCallback = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            openMenuDialog(slug, {
                hideNextPrev: disableMenuNextPrev,
                transitionSource: TransitionTypes.TILE,
            })
        },
        [slug, disableMenuNextPrev],
    )

    const onGoToRoomCallback = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            if (isAnonymous) return
            if (loungeSlug) {
                // if we're in the lounge, set the surrogate
                setLoungeSurrogate(id, loungeSlug)
            } else {
                openRoom(slug, {
                    transitionSource: TransitionTypes.TILE,
                })
            }
        },
        [isAnonymous, id, slug, loungeSlug],
    )

    const onOpenNote = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            goToNotes(name)
        },
        [id, name],
    )

    const shouldDisplayLargePreviewButton =
        canDisplayLargePreviewButton &&
        (showVideoPreview || isTouchDevice || isPreviewPinned)

    const shouldDisplayPinButton =
        canDisplayPinButton &&
        (showVideoPreview || isTouchDevice || isRoomPinned)

    // useEffect(() => {

    //     if (!ref.current) {
    //         return
    //     }

    //     setGridRowRefState(ref.current, index, ListType.DEFAULT)

    //     return () => {
    //         removeGridRowRefsState(index, ListType.DEFAULT)
    //     }

    // }, [])
    const dragStart = useCallback(
        (event) => {
            event.dataTransfer.effectAllowed = 'move'
            if (snapRef.current && SNAPVIDEOSTATE[id]?.playing) {
                snapRef.current.width = 128
                snapRef.current.height = 72
                snapRef.current.className = ''
                event.dataTransfer.setDragImage(snapRef.current, 64, 36)
            }
            event.dataTransfer.setData('text/plain', id)
            showDragHelper(id)
        },
        [id],
    )
    const dragMove = useCallback((event) => {
        const { clientX, clientY } = event
        highlightDragHelper(clientX, clientY)
    }, [])
    const dragEnd = useCallback(() => {
        snapRef.current.width = snapSize.current.width
        snapRef.current.height = snapSize.current.height
        snapRef.current.className = snapSize.current.className
        hideDragHelper()
    }, [])
    const onLoad = useCallback((event) => {
        if (snapSize.current) return
        snapRef.current = event.target as HTMLImageElement
        snapSize.current = {
            className: snapRef.current.className,
            width: snapRef.current.width,
            height: snapRef.current.height,
        }
    }, [])

    return (
        <div
            data-testid="Container__ModelDisplay"
            css={rootStyles}
            ref={ref}
            key={id}
            id={`tile-${id}`}
        >
            <div
                data-testid="Container__Preview"
                onClick={onGoToRoomCallback}
                onMouseOver={isTouchDevice ? null : () => setVideoPreview(true)}
                onMouseLeave={
                    isTouchDevice ? null : () => setVideoPreview(false)
                }
                css={previewContainerStyles}
                style={{
                    border: highlight ? `1px solid ${highlight}` : null,
                    height: displayHeight,
                }}
                draggable
                onDragStart={dragStart}
                onDrag={dragMove}
                onDragEnd={dragEnd}
            >
                <VideoCover />
                <LiveSnapStream
                    suppressVideo={
                        passiveMode ||
                        (!showVideoPreview && !shouldForceVideoPlay)
                    }
                    onPreviewLoaded={onLoad}
                    isBroadcasting={isBroadcasting}
                    compact={stylesMaxWidth < 150}
                    videoID={VideoType.ModelTile}
                    isTruePrivate={isTruePrivate}
                    isPrivateShow={isPrivateShow}
                    isGroupShow={isGroupShow}
                    isAnonymous={isAnonymous}
                    staticImage={staticImage}
                    isClubShow={isClubShow}
                    height={displayHeight}
                    isOffline={isOffline}
                    isNGVideo={isNGVideo}
                    width={columnWidth}
                    isLounge={isLounge}
                    isModel={isModel}
                    channel={channel}
                    server={server}
                    isAway={isAway}
                    banned={isBanned}
                    phase={phase}
                    noBackground
                    hideControls
                    videoMuted
                    id={id}
                />
                {(shouldDisplayLargePreviewButton || canDisplayNotesButton) && (
                    <div css={previewPlayerContainer}>
                        {shouldDisplayLargePreviewButton && (
                            <LargeFloatingPreview
                                size={stylesUtilities('iconSize')}
                                id={id}
                            />
                        )}

                        {canDisplayNotesButton && (
                            <NotesButton
                                size={stylesUtilities('iconSize')}
                                filled
                                tooltipPlacement={'bottom'}
                                title="Open Notes"
                                onClick={onOpenNote}
                                shadow
                            />
                        )}
                    </div>
                )}
                {(shouldDisplayPinButton || canDisplayRecentlyVisitedBadge) && (
                    <div css={actionContainerStyles}>
                        {shouldDisplayPinButton && (
                            <PinButton
                                title={
                                    (isRoomPinned ? 'Unpin' : 'Pin') + ' Model'
                                }
                                size={stylesUtilities('iconSize')}
                                filled={isRoomPinned}
                                onClick={onPinCallback}
                            />
                        )}
                        {canDisplayRecentlyVisitedBadge && (
                            <Container
                                title="Recently Visited"
                                styleOverrides={{
                                    margin: shouldDisplayPinButton
                                        ? getStyle('roomIconOffset') || 8
                                        : 8,
                                }}
                                tooltipPlacement="bottom"
                                shadow
                            >
                                <RoomIcon
                                    size={stylesUtilities('iconSize')}
                                    color="#fff"
                                    filled
                                />
                            </Container>
                        )}
                    </div>
                )}
                {canDisplayBadges && (
                    <div
                        css={badgeContainerStyles}
                        style={{
                            marginBottom: stylesBadgeStatus('offsetY'),
                            marginLeft: stylesBadgeStatus('offsetX'),
                        }}
                    >
                        <div style={{ display: 'flex' }}>
                            {candisplayFriendBadge && (
                                <FriendBadge
                                    size={stylesBadgeStatus('iconSize')}
                                />
                            )}
                            {canDisplayBookmarkBadge && (
                                <BookmarkBadge
                                    size={stylesBadgeStatus('iconSize')}
                                />
                            )}
                            {canDisplayPopularBadge && (
                                <PopularBadge
                                    size={stylesBadgeStatus('iconSize')}
                                />
                            )}
                            {canDisplayTrendingBadge && (
                                <TrendingBadge
                                    size={stylesBadgeStatus('iconSize')}
                                />
                            )}
                            {canDisplayPriorMissMFCBadge && (
                                <PriorMissMFCBadge
                                    styleOverrides={{
                                        height: stylesPriorMissMFCBadge(
                                            'height',
                                        ),
                                        width: stylesPriorMissMFCBadge('width'),
                                        marginLeft: 2,
                                    }}
                                    priorMissMfcRank={priorMissMfcRank}
                                />
                            )}
                        </div>
                        {(canDisplayMissMFCBadge ||
                            canDisplayNewModelBadge) && (
                            <NewAndMissMFCBadges
                                styleOverrides={{
                                    fontSize: stylesBadgeNewMissMFC('fontSize'),
                                    width: '100%',
                                    marginTop: 3,
                                }}
                                showMissMFC={canDisplayMissMFCBadge}
                                showNew={canDisplayNewModelBadge}
                                missMFCRank={missMFCRank}
                            />
                        )}
                    </div>
                )}
                <div
                    css={modelMenuStyles}
                    style={{
                        visibility:
                            !isAnonymous && (showVideoPreview || isTouchDevice)
                                ? null
                                : 'hidden',
                    }}
                >
                    <ButtonModelMenu onClick={onOpenMenuCallback} />
                </div>
            </div>
            {canDisplayBanners && (
                <>
                    {canDisplayMissMFCBanner && (
                        <MissMFCBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                            }}
                            rankStyleOverrides={{
                                margin: stylesBannerMissMFCRank('margin'),
                            }}
                            missMFCPrize={missMFCPrize}
                            missMFCRank={missMFCRank}
                        />
                    )}
                    {canDisplayTrendingBanner && (
                        <TrendingBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                            }}
                            diffStyleOverrides={{
                                margin: stylesBannerTrendingDiff('margin'),
                            }}
                            text={stylesBannerTrending('text')}
                            diff={trendingDiff}
                        />
                    )}
                    {canDisplayPopularBanner && (
                        <PopularBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                            }}
                            viewersStyleOverrides={{
                                margin: stylesBannerPopularViewers('margin'),
                            }}
                            iconSize={stylesBanner('iconSize')}
                            text={stylesBannerPopular('text')}
                            viewers={viewers}
                        />
                    )}
                    {canDisplayGroupShowBanner &&
                        (isGroupShow ||
                            isGroupShowPending ||
                            groupShowRoomCount) && (
                            <GroupShowBanner
                                styleOverrides={{
                                    fontSize: stylesBannerPX('fontSize'),
                                    height: stylesBannerPX('height'),
                                }}
                                iconSize={stylesBanner('iconSize')}
                                roomCount={groupShowRoomCount}
                                isLive={isGroupShow}
                            />
                        )}
                    {canDisplayNewModelBanner && (
                        <NewModelBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                            }}
                            timeStyleOverrides={{
                                margin: stylesBannerNewTime('margin'),
                            }}
                            compact={columnWidth < 135}
                            creation={creation}
                        />
                    )}
                </>
            )}
            <ModelName
                modelId={id}
                onClick={onGoToRoomCallback}
                styleOverrides={{
                    borderTop: canDisplayBanners
                        ? `1px solid ${palette.drawerDivider}`
                        : null,
                    // canDisplayBanners
                    //     ? `1px dotted ${
                    //           hexRGBA(modelColor, 0.3) || 'rgba(0,0,0, 0.3)'
                    //       }`
                    //     : '1px solid #FFFFFF',
                    justifyContent:
                        canDisplayBanners || stylesName('center')
                            ? 'center'
                            : 'flex-start',
                    fontSize: stylesNamePX('fontSize')(),
                    height: stylesNamePX('height'),
                }}
                isAnonymous={isAnonymous}
                color={color}
                name={name}
            />
        </div>
    )
})

export default Preview
export { Preview }
